import rawConfig from "../appsettings.json";

/**
 * Runtime application configuration.
 *
 * The API base URLs are NOT baked into the bundle anymore. They are injected by
 * the server at request time as `window.__APP_CONFIG__` (see Views/Main/Index.cshtml),
 * which reads them from `IConfiguration` — the very same values that drive the
 * Content-Security-Policy `connect-src` in Startup.cs. One build artifact therefore
 * works in every environment (dev, prod, …) and the CSP can never drift from the
 * endpoint the bundle actually calls.
 *
 * `appsettings.json` only provides the dev / SSR / test fallback.
 *
 * Migration note: 60+ legacy modules read `config.ApiSettings.EndPoint` (etc.)
 * directly off the imported `appsettings.json` object, always at call time. To
 * avoid editing every one of them, we overwrite those fields in place on the
 * shared module object once, at boot, before any service call runs. New code
 * should import {@link appConfig} instead of `appsettings.json`.
 */
export interface IRuntimeAppConfig {
    apiEndpoint?: string;
    hubEndpoint?: string;
    chatHubEndpoint?: string;
    appEndpoint?: string;
    /** "sports" on the Fidava Sports host (see core/brand.ts). */
    brand?: string;
    /** The Fidava Sports host (e.g. sports.fidava-pt.com); empty when not configured. */
    sportsHost?: string;
    /** Web-form token for the marketing site's brochure e-mail capture (Marketing:BrochureFormToken); empty hides it. */
    siteLeadFormToken?: string;
    /** Playground only: the real API that backs its access form. Everything
     *  else in the playground stays on the dead `apiEndpoint`. */
    playgroundAccessEndpoint?: string;
}

declare global {
    interface Window {
        __APP_CONFIG__?: IRuntimeAppConfig;
    }
}

// `as unknown as` (not `any`) widens the JSON's inferred literal types to mutable
// strings so we can overwrite them; it points at the same shared object instance.
interface IMutableEndpointConfig {
    ApiSettings: { EndPoint: string; EndPointHub: string; EndPointChatHub: string };
    AppSettings: { EndPoint: string };
}

const config = rawConfig as unknown as IMutableEndpointConfig;

function applyRuntimeConfig(): void {
    if (typeof window === "undefined" || !window.__APP_CONFIG__) {
        // SSR / tests / no injection: keep appsettings.json fallback values.
        return;
    }

    const runtime = window.__APP_CONFIG__;
    if (runtime.apiEndpoint) {
        config.ApiSettings.EndPoint = runtime.apiEndpoint;
    }
    if (runtime.hubEndpoint) {
        config.ApiSettings.EndPointHub = runtime.hubEndpoint;
    }
    if (runtime.chatHubEndpoint) {
        config.ApiSettings.EndPointChatHub = runtime.chatHubEndpoint;
    }
    if (runtime.appEndpoint) {
        config.AppSettings.EndPoint = runtime.appEndpoint;
    }
}

applyRuntimeConfig();

/**
 * Typed accessors for the resolved endpoints. Getters (not captured values) so
 * the resolution above is always reflected, regardless of import order.
 */
export const appConfig = {
    get apiEndpoint(): string {
        return config.ApiSettings.EndPoint;
    },
    get hubEndpoint(): string {
        return config.ApiSettings.EndPointHub;
    },
    get chatHubEndpoint(): string {
        return config.ApiSettings.EndPointChatHub;
    },
    get appEndpoint(): string {
        return config.AppSettings.EndPoint;
    }
};
